import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { patchRecord } from '../../registryApi';
import QuestionField from '../components/QuestionField';
import ChoiceGroup from '../components/ChoiceGroup';
import FileDropList from '../components/FileDropList';
import StepFooter from '../components/StepFooter';
import { CONTACT_MODE, FILE_KIND } from '../../registryChoices';
import { armSizeMismatch, duplicateLabelIds } from '../treatmentChecks';
import RemoveTreatmentDialog from '../RemoveTreatmentDialog';

const TREATMENTS_API = '/registry/treatments';
const REQUIRED = 'This question is required.';
const DUPLICATE_LABEL = 'Each treatment needs its own label. Another treatment already uses this one.';

const ArmBadge = ({ children, muted = false }) => (
  <Avatar
    sx={{
      width: 22,
      height: 22,
      fontSize: '0.6875rem',
      bgcolor: muted ? 'grey.400' : 'primary.main',
      color: muted ? 'text.primary' : 'primary.contrastText',
    }}
  >
    {children}
  </Avatar>
);

const ArmTitle = ({ badge, muted, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <ArmBadge muted={muted}>{badge}</ArmBadge>
    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.dark' }}>
      {children}
    </Typography>
  </Box>
);

// Edits update the parent's treatments immediately (so Next validates what's on
// screen) and save to the server when the field loses focus.
const useArmSaver = (treatment, onLocalChange) => {
  const [error, setError] = useState('');
  const save = async (field, value) => {
    setError('');
    try {
      await patchRecord(TREATMENTS_API, treatment.id, { [field]: value });
    } catch (e) {
      setError(e.message || 'Could not save this treatment.');
    }
  };
  const change = (field, value) => onLocalChange(treatment.id, field, value);
  return { error, save, change };
};

const toNumberOrNull = (value) => (value === '' || value === null ? null : Number(value));

const TreatmentArm = ({
  treatment,
  index,
  total,
  showErrors,
  isDuplicateLabel,
  onLocalChange,
  onRemove,
  canRemove,
  experimentId,
  files,
  setFiles,
}) => {
  const [expanded, setExpanded] = useState(index === 0);
  const { error, save, change } = useArmSaver(treatment, onLocalChange);
  const err = (missing) => (showErrors && missing ? REQUIRED : '');
  // A clashing label is flagged right away, not just after "Next".
  const labelError = err(!treatment.treatment_label.trim()) || (isDuplicateLabel ? DUPLICATE_LABEL : '');
  const isIncomplete =
    !treatment.treatment_label.trim() ||
    isDuplicateLabel ||
    !treatment.treatment_description.trim() ||
    treatment.modes.length === 0;
  const creatives = files.filter((f) => f.kind === FILE_KIND.CREATIVE && f.treatment === treatment.id);

  return (
    <Accordion
      expanded={expanded || (showErrors && isIncomplete)}
      onChange={() => setExpanded(!expanded)}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <ArmTitle badge={index + 1}>
            Treatment {index + 1} of {total}
            {!expanded && treatment.treatment_label ? ` — ${treatment.treatment_label}` : ''}
          </ArmTitle>
          <IconButton
            size="small"
            aria-label={`Remove treatment ${index + 1}`}
            disabled={!canRemove}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(treatment);
            }}
            onFocus={(e) => e.stopPropagation()}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2.75}>
          <QuestionField
            label="Please provide a label for this treatment"
            required
            helper="(e.g., canvass, canvass and phone, etc)"
            error={labelError}
          >
            <TextField
              hiddenLabel
              fullWidth
              size="small"
              value={treatment.treatment_label}
              onChange={(e) => change('treatment_label', e.target.value)}
              onBlur={(e) => save('treatment_label', e.target.value)}
              error={Boolean(labelError)}
            />
          </QuestionField>

          <QuestionField
            label="Please provide a brief description for this treatment."
            required
            error={err(!treatment.treatment_description.trim())}
          >
            <TextField
              hiddenLabel
              fullWidth
              multiline
              minRows={3}
              value={treatment.treatment_description}
              onChange={(e) => change('treatment_description', e.target.value)}
              onBlur={(e) => save('treatment_description', e.target.value)}
              error={Boolean(err(!treatment.treatment_description.trim()))}
            />
          </QuestionField>

          <QuestionField
            label="What modes will you be using to contact voters in this treatment?"
            required
            error={err(treatment.modes.length === 0)}
          >
            <ChoiceGroup
              multiple
              layout="columns"
              options={CONTACT_MODE}
              value={treatment.modes}
              onChange={(modes) => {
                change('modes', modes);
                save('modes', modes);
              }}
            />
          </QuestionField>

          <QuestionField label="How many units are in this treatment condition?">
            <TextField
              type="number"
              hiddenLabel
              size="small"
              value={treatment.initial_n ?? ''}
              onChange={(e) => change('initial_n', toNumberOrNull(e.target.value))}
              onBlur={(e) => save('initial_n', toNumberOrNull(e.target.value))}
              inputProps={{ min: 0 }}
              sx={{ width: 220 }}
            />
          </QuestionField>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <QuestionField label="If this treatment begins after the start of the program, please note when it starts.">
              <TextField
                type="date"
                hiddenLabel
                size="small"
                value={treatment.treatment_start_date || ''}
                onChange={(e) => change('treatment_start_date', e.target.value || null)}
                onBlur={(e) => save('treatment_start_date', e.target.value || null)}
                sx={{ width: 220 }}
              />
            </QuestionField>
            <QuestionField label="If this treatment ends before the end of the program, please note when it ends.">
              <TextField
                type="date"
                hiddenLabel
                size="small"
                value={treatment.treatment_end_date || ''}
                onChange={(e) => change('treatment_end_date', e.target.value || null)}
                onBlur={(e) => save('treatment_end_date', e.target.value || null)}
                sx={{ width: 220 }}
              />
            </QuestionField>
          </Box>

          <QuestionField
            label="Please upload any creatives for this treatment."
            helper="(e.g., a canvassing or phone script, a relational training deck, a mailer, etc.)"
          >
            <FileDropList
              experimentId={experimentId}
              kind={FILE_KIND.CREATIVE}
              treatmentId={treatment.id}
              files={creatives}
              onChange={(updated) =>
                setFiles([
                  ...files.filter((f) => !(f.kind === FILE_KIND.CREATIVE && f.treatment === treatment.id)),
                  ...updated,
                ])
              }
            />
          </QuestionField>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const ControlArm = ({ control, onLocalChange }) => {
  const { error, save, change } = useArmSaver(control, onLocalChange);
  return (
    <Accordion expanded disableGutters>
      <AccordionSummary sx={{ cursor: 'default !important' }}>
        <ArmTitle badge="C" muted>
          Control — No Contact
        </ArmTitle>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <QuestionField label="How many units are in the control condition?">
          <TextField
            type="number"
            hiddenLabel
            size="small"
            value={control.initial_n ?? ''}
            onChange={(e) => change('initial_n', toNumberOrNull(e.target.value))}
            onBlur={(e) => save('initial_n', toNumberOrNull(e.target.value))}
            inputProps={{ min: 0 }}
            sx={{ width: 220 }}
          />
        </QuestionField>
      </AccordionDetails>
    </Accordion>
  );
};

const Step4Treatments = ({
  treatments,
  setTreatments,
  numTreatments,
  totalN,
  showErrors,
  onNext,
  onBack,
  onRemoveTreatment,
  experimentId,
  files,
  setFiles,
}) => {
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  const arms = treatments.filter((t) => !t.is_control).sort((a, b) => a.position - b.position);
  const control = treatments.find((t) => t.is_control);
  const duplicates = duplicateLabelIds(treatments);
  const sizeMismatch = armSizeMismatch(treatments, totalN);

  const confirmRemoval = async () => {
    setRemoving(true);
    try {
      await onRemoveTreatment(pendingRemoval);
    } finally {
      setRemoving(false);
      setPendingRemoval(null);
    }
  };

  const handleLocalChange = (treatmentId, field, value) =>
    setTreatments((prev) => prev.map((t) => (t.id === treatmentId ? { ...t, [field]: value } : t)));

  return (
    <>
      <Stack spacing={2}>
        {arms.length === 0 && (
          <Alert severity="info">
            Set how many treatments you&apos;re testing on the previous page to add them here.
          </Alert>
        )}
        {sizeMismatch && (
          <Alert severity="warning">
            The treatment and control group sizes add up to {sizeMismatch.sum.toLocaleString()}, but the
            total N on the previous page is {sizeMismatch.total.toLocaleString()}. Please check that these
            match.
          </Alert>
        )}
        {arms.map((treatment, index) => (
          <TreatmentArm
            key={treatment.id}
            treatment={treatment}
            index={index}
            total={numTreatments || arms.length}
            showErrors={showErrors}
            isDuplicateLabel={duplicates.has(treatment.id)}
            onLocalChange={handleLocalChange}
            onRemove={setPendingRemoval}
            canRemove={arms.length > 1}
            experimentId={experimentId}
            files={files}
            setFiles={setFiles}
          />
        ))}
        {control && <ControlArm control={control} onLocalChange={handleLocalChange} />}
      </Stack>

      <StepFooter onBack={onBack} onNext={onNext} />
      <RemoveTreatmentDialog
        open={Boolean(pendingRemoval)}
        treatmentNumber={pendingRemoval?.position}
        treatmentLabel={pendingRemoval?.treatment_label}
        loading={removing}
        onConfirm={confirmRemoval}
        onCancel={() => setPendingRemoval(null)}
      />
    </>
  );
};

export default Step4Treatments;
